import React from "react";
import { useTranslation } from "react-i18next";
import { Activity, Plus, AlertCircle } from "lucide-react";
import ExpandableStockCard from "./ExpandableStockCard";
import StockerSignalPanel from "../DashboardComponents/StockerSignalPanel";
import TechnicalContextPanel from "../DashboardComponents/TechnicalContext";
import { CardLoadingSkeleton } from "./LoadingStates";

/**
 * Primary grid: watchlist stock cards (left, 2/3) + signal/technical
 * context sidebar (right, 1/3). Extracted verbatim from
 * UnifiedStockerDashboard.jsx's "PRIMARY GRID" block.
 */
const WatchlistSection = ({
  watchlistData,
  dataLoading,
  expandedStock,
  onStockToggle,
  onCreatePost,
  chartData,
  indicators,
  currentTimeFrame,
  currentIndicators,
  onTimeFrameChange,
  onIndicatorsChange,
  contextRadarState,
  stockerSignalState,
  activeSignal,
  activeSignalLoading,
  activeSignalError,
  filteredTechnicalContext,
  onAddTicker,
}) => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* LEFT — watchlist cards */}
      <div className="lg:col-span-2 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-400" />
            <h2 className="text-base font-bold text-gray-800 dark:text-slate-200">
              {t("dashboard.unifiedDashboard.yourWatchlist")}
            </h2>
            <span className="text-xs text-gray-500 dark:text-slate-500">
              {t("dashboard.unifiedDashboard.tickersCount", { count: watchlistData.length })}
            </span>
          </div>
          <button
            onClick={onAddTicker}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600/10 hover:bg-blue-600/20 text-blue-300 rounded-lg text-sm font-medium transition-colors border border-blue-500/20"
          >
            <Plus className="w-4 h-4" />
            {t("dashboard.unifiedDashboard.addTicker")}
          </button>
        </div>

        {dataLoading && watchlistData.length === 0 ? (
          <CardLoadingSkeleton count={3} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {watchlistData.map((stock) => {
              const isActive = expandedStock === stock.ticker;

              const tickerEvents =
                contextRadarState?.byTicker?.[stock.ticker]?.data ??
                contextRadarState?.watchlist?.data?.filter((e) => e.ticker === stock.ticker) ??
                [];

              const tickerSignal = stockerSignalState[stock.ticker]?.data || null;

              return (
                <ExpandableStockCard
                  key={stock.ticker}
                  stock={stock}
                  isExpanded={isActive}
                  onToggle={() => onStockToggle(stock.ticker)}
                  onCreatePost={onCreatePost}
                  chartData={isActive ? chartData : []}
                  indicators={isActive ? indicators : {}}
                  currentTimeFrame={currentTimeFrame}
                  currentIndicators={currentIndicators}
                  onTimeFrameChange={onTimeFrameChange}
                  onIndicatorsChange={onIndicatorsChange}
                  contextEvents={tickerEvents}
                  signal={tickerSignal}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* RIGHT — signal + technical context sidebar */}
      <div className="space-y-4 lg:col-span-1">
        <StockerSignalPanel
          signal={activeSignal}
          loading={activeSignalLoading}
          error={activeSignalError}
          activeStock={expandedStock}
        />

        <TechnicalContextPanel items={filteredTechnicalContext} activeStock={expandedStock} />

        {activeSignalError && (
          <div className="bg-red-100 dark:bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-700 dark:text-red-300 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs font-semibold text-red-800 dark:text-red-200">
                  {t("dashboard.unifiedDashboard.signalUnavailableTitle")}
                </div>
                <div className="text-[10px] text-red-700/70 dark:text-red-200/70 mt-0.5">
                  {t("dashboard.unifiedDashboard.signalUnavailableSubtitle")}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WatchlistSection;
